import { Button, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import { useFetchSourceDocuments } from 'hooks/use-source-documents'
import { SourceDocument } from 'api/model'
import { useActiveOrganization } from 'hooks/use-organization'
import { API_KEY, CLIENT_ID } from 'components/MyTwin/MyTwinFiles'
import useDrivePicker from 'react-google-drive-picker'
import { formatGoogleDrivePickerDocuments } from 'components/MyTwin/utils'
import { sourcesNewDocument } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { toastAtom } from 'state'
import { useSetRecoilState } from 'recoil'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { LoadingButton } from '@mui/lab'
import { COLORS } from 'app-constants'

const AttachDocumentsPopover = ({
  existingDocuments,
  onAddDocuments,
  onClose,
}: {
  onAddDocuments: (sourceDocument: SourceDocument[]) => void
  existingDocuments: SourceDocument[]
  onClose: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [selectedDocuments, setSelectedDocuments] = useState<SourceDocument[]>([])
  useEffect(() => {
    setSelectedDocuments(existingDocuments)
  }, [existingDocuments])

  const [openPicker] = useDrivePicker()

  const [isUploadingDocuments, setIsUploadingDocuments] = useState<boolean>(false)

  const handleOpenPicker = () => {
    openPicker({
      clientId: CLIENT_ID ?? '',
      developerKey: API_KEY ?? '',
      viewId: 'DOCS',
      // token: user?.google_oauth_token ?? '',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      customScopes: ['https://www.googleapis.com/auth/drive.readonly'],
      // customViews: customViewsArray, // custom view
      callbackFunction: async (data) => {
        if (!activeOrganization) {
          return
        }
        if (data.docs) {
          setIsUploadingDocuments(true)
          const formattedDocs = formatGoogleDrivePickerDocuments(data.docs)

          // do not add duplicate existing documents
          const filteredDocs = formattedDocs.filter((doc) => {
            return !sourceDocuments.find((d) => d.document_id === doc.document_id)
          })
          const hasDuplicateDocuments = formattedDocs.length !== filteredDocs.length

          try {
            const response = await sourcesNewDocument(activeOrganization, formattedDocs)

            // should only add documents that were not already in the list
            const filteredResponse = response.data.filter((doc) => {
              return !sourceDocuments.find((d) => d.document_id === doc.document_id)
            })
            setSourceDocuments([...sourceDocuments, ...filteredResponse])
            setSelectedDocuments([...selectedDocuments, ...filteredResponse])

            if (hasDuplicateDocuments) {
              setToast({
                title: 'Successfully updated documents',
                severity: 'success',
              })
            } else {
              setToast({
                title: 'Successfully added documents',
                severity: 'success',
              })
            }
          } catch {
            setToast({
              title: 'Failed to add documents',
              severity: 'error',
            })
          } finally {
            setIsUploadingDocuments(false)
          }
        }
      },
    })
  }

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const { sourceDocuments, isLoading: isLoadingSourceDocuments, setSourceDocuments } = useFetchSourceDocuments()
  const filteredSourceDocuments = sourceDocuments.filter((sd) => {
    return sd.name.toLowerCase().includes(searchText.toLowerCase()) || selectedDocuments.find((s) => s.id === sd.id)
  })

  if (isLoadingSourceDocuments) {
    return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
  }

  return (
    <Stack py={2} px={1} width="320px">
      <Typography textAlign="center" variant="h5">
        Documents
      </Typography>

      <TextField
        placeholder="Search for a document"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  marginRight: '-10px',
                  color: COLORS.GRAY_3,
                  height: '18px',
                  width: '18px',
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          my: 1,
          input: {
            color: 'secondary.main',
          },
          '& ::placeholder': {
            color: 'secondary.main',
          },
        }}
      />

      <Stack
        sx={{
          maxHeight: '300px',
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}>
        {filteredSourceDocuments.length === 0 && (
          <Typography
            variant="pXS"
            textAlign="center"
            my={4}
            sx={{
              color: COLORS.GRAY_2,
            }}>
            No documents found
          </Typography>
        )}
        <Stack spacing={1}>
          {filteredSourceDocuments.map((f) => {
            const isSelected = selectedDocuments.find((s) => s.id === f.id)

            return (
              <Stack
                direction="row"
                alignItems="center"
                key={f.id}
                onClick={() => {
                  const selectedSourceDocument = selectedDocuments.find((s) => s.id === f.id)
                  if (selectedSourceDocument) {
                    setSelectedDocuments(selectedDocuments.filter((t) => t.id !== f.id))
                  } else {
                    setSelectedDocuments([...selectedDocuments, f])
                  }
                }}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: isSelected ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: COLORS.GRAY_8,
                  },
                }}>
                {isSelected ? (
                  <CheckBoxIcon
                    sx={{
                      fill: COLORS.JELLED_PRIMARY_BLUE,
                    }}
                  />
                ) : (
                  <CheckBoxOutlineBlankIcon
                    sx={{
                      fill: COLORS.GRAY_2,
                    }}
                  />
                )}
                <Typography variant="pSM" ml={1} color="secondary">
                  {f.name}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
        <LoadingButton
          loadingIndicator="Uploading..."
          loading={isUploadingDocuments}
          variant="outlined"
          onClick={handleOpenPicker}
          sx={{
            color: COLORS.GRAY_0,
            border: `1px solid ${COLORS.GRAY_5}`,
            '&:hover': {
              borderWidth: '1px',
            },
          }}>
          Add new document
        </LoadingButton>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onAddDocuments(selectedDocuments)
            onClose()
          }}>
          Done
        </Button>
      </Stack>
    </Stack>
  )
}

export default AttachDocumentsPopover
